import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    TextField,
    MenuItem,
    Button,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { createCompanyUser, fetchUserRoles, updateCompanyUser } from "../../features/companyUser/companyUserThunk";
import {
    selectIsLoading,
    selectError,
    selectRoles,
    selectIsRolesLoading,
    selectRolesError,
    selectUsers
} from "../../features/companyUser/companyUserSlice";
import { selectCompanies } from "../../features/company/companySlice";
import { fetchCompanies } from "../../features/company/companyThunk";
// Validation Schema
const schema = yup.object().shape({
    name: yup.string().required("Nome collaboratore is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    role_id: yup.string().required("Tipo di profilo is required"),
    company_id: yup.string().required("Associazione azienda is required"),
});
const UtentiForm = ({ onClose, selectedUser }) => {
    const dispatch = useDispatch();
    // Selectors
    const authUser = useSelector(state => state.auth.user);
    const isLoading = useSelector(selectIsLoading);
    const error = useSelector(selectError);
    const companies = useSelector(selectCompanies);
    const CompanyUser = useSelector(selectUsers)
    const roles = useSelector(selectRoles);
    const isRolesLoading = useSelector(selectIsRolesLoading);
    const rolesError = useSelector(selectRolesError);
    // Fetch Companies and Roles once on mount
    useEffect(() => {
        dispatch(fetchCompanies());
        dispatch(fetchUserRoles());
    }, [dispatch]);
    
    // React Hook Form
    const {
        control,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: "",
            email: "",
            role_id: "",
            company_id: "",
        }
    });
    useEffect(() => {
        if (!selectedUser && authUser?.company_id) {
          setValue('company_id', authUser.company_id);
        }
      }, [selectedUser, authUser, setValue]);
      
    // Prefill form if selectedUser exists
    useEffect(() => {
        if (selectedUser) {
            reset({
                name: selectedUser.name || "",
                email: selectedUser.email || "",
                role_id: selectedUser.role_id || "",
                company_id: selectedUser.company_id || "",
            });
        }
    }, [selectedUser, reset]);
    // Submit handler
    const onSubmit = (data) => {
        if (selectedUser) {
            const payload = {
                userId: selectedUser.id,
                userData: data,
            };
            dispatch(updateCompanyUser(payload))
                .unwrap()
                .then(() => {
                    console.log("User updated successfully");
                    if (onClose) onClose();
                    // Optional: show toast/snackbar
                })
                .catch((error) => {
                    console.error("Failed to update user:", error.message);
                    // Optional: show error in UI
                });
        } else {
            dispatch(createCompanyUser(data))
                .unwrap()
                .then(() => {
                    console.log("User created successfully");
                    if (onClose) onClose();
                })
                .catch((error) => {
                    console.error("Failed to create user:", error.message);
                });
        }
    };
    // Fallback role options
    const fallbackRoleOptions = [
        { id: "ADMIN", name: "Amministratore" },
        { id: "COLLABORATOR", name: "Collaboratore" },
        { id: "OPERATOR", name: "Operatore" }
    ];
    const roleOptions = (roles?.length > 0 ? roles : fallbackRoleOptions).filter(
        (role) => role.id !== "SUPERADMIN"
    );

    return (
        <>
            {isRolesLoading ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                    <CircularProgress />
                </div>
            ) : (
                <>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {rolesError && <Alert severity="warning" sx={{ mb: 2 }}>Could not load user roles. Using default values.</Alert>}
                    <form className="AziendaForm" onSubmit={handleSubmit(onSubmit)}>
                        <div className="AziendaForm__inner">
                            {/* Name */}
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Nome collaboratore"
                                        fullWidth
                                        margin="normal"
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                        disabled={isLoading}
                                    />
                                )}
                            />
                            {/* Email */}
                            <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Email"
                                        fullWidth
                                        margin="normal"
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        disabled={isLoading}
                                    />
                                )}
                            />
                            {/* Role */}
                            <Controller
                                name="role_id"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label="Tipo di profilo"
                                        fullWidth
                                        margin="normal"
                                        error={!!errors.role_id}
                                        helperText={errors.role_id?.message}
                                        disabled={isLoading || isRolesLoading || !["SUPERADMIN", "ADMIN"].includes(authUser?.role_id)}
                                    >
                                        {isRolesLoading ? (
                                            <MenuItem disabled>Loading roles...</MenuItem>
                                        ) : (
                                            roleOptions.map((role) => (
                                                <MenuItem key={role.id} value={role.id}>
                                                    {role.name || role.role_name}
                                            
                                                </MenuItem>
                                            ))
                                        )}
                                    </TextField>
                                )}
                            />

                            <Controller
                                name="company_id"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label="Associazione azienda"
                                        fullWidth
                                        margin="normal"
                                        error={!!errors.company_id}
                                        helperText={errors.company_id?.message}
                                        disabled={authUser?.role_id !== "SUPERADMIN" || isLoading}
                                    >
                                        {companies?.map((company) => (
                                            <MenuItem key={company.id} value={company.id}>
                                                {company.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />

                            {/* Submit */}
                            <div className="btnPart1">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isLoading || isRolesLoading}
                                >
                                    {isLoading ? <CircularProgress size={24} /> : selectedUser ? "Aggiorna dati" : "Salva dati"}
                                </Button>
                            </div>
                        </div>
                    </form>
                </>
            )
            }
        </>
    );
};
export default UtentiForm;